<template>
  <div class="uploader-container">
    <div class="upload-progress-container">
      <div uk-grid>
        <div class="uk-width-expand uk-flex uk-flex-middle">
          <span class="file-name-style uk-text-truncate">{{fileName}}</span>
        </div>
        <div class="uk-width-auto uk-flex uk-flex-middle uk-flex-right">
        <span class="upload-progress-style">
        {{status}}
          <span
            class="add-pointer"
            @mouseover="resolveHover(true)"
            @mouseleave="resolveHover(false)"
            @click="cancel">
            <img v-if="!hover" :src="cancelIcon"/>
            <img v-else :src="cancelIconHover"/>
          </span>
      </span>
        </div>
      </div>
      <div class="progress-bar-container">
        <progress
          v-if="uploadStatus"
          class="uk-progress bar"
          :value="uploadStatus"
          max="100">
        </progress>
        <div
          v-else
          class="uk-progress bar loader">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import * as misc from '../../../../enums/misc'
  import iconUtil from '../../../../utils/icon-util'
  import fileUtil from '../../../../utils/file-util'
  import dateUtil from '../../../../utils/date-util'
  import uploadError from '../../../../utils/upload-error-util'
  import {mapActions, mapGetters} from 'vuex'
  import * as event from '../../../../enums/mixpanel-event'
  import metrics from '../../../../services/metrics'
  import mixpanel from '../../../../services/mixpanel'
  import csSdkManager from '../../../../managers/cs-sdk-manager'

  export default {
    name: 'uploader',
    data() {
      return {
        cancelIcon: iconUtil.resolve(misc.CLOSE_SLATE),
        cancelIconHover: iconUtil.resolve(misc.CLOSE_BLUE),
        hover: false,
        uploadStatus: null,
        processingStatus: null,
        contentId: null,
        pollProcessing: false
      }
    },
    props: ['file', 'resourceId'],
    methods: {
      ...mapActions(['uploadContent', 'cancelResourceUpload', 'transactionStatus', 'putMetaData', 'updateResourceFile']),
      cancel () {
        if (this.contentId) {
          let metadata = {
            expiry: dateUtil.futureDate(5)
          }
          this.putMetaData({metadata: metadata, contentId: this.contentId, filters: null})
            .then(() => {
              this.contentId = null
            })
            .catch(err => {
              metrics.sendMetric(metrics.CATEGORIES.THIRD_PARTY_CALLS, metrics.CP_TAGS.RESOURCE_EXPIRY_ERROR, 1, metrics.TYPE.FAILURE_COUNTER, err)
            })
        } else {
          this.cancelResourceUpload()
        }
        this.pollProcessing = false
        this.uploadStatus = null
        this.$emit('cancel')
      },
      uploadResource () {
        this.processingStatus = null
        csSdkManager.getCSClient({accessToken: this.accessToken}).uploadNewResource(this.file)
          .progress(p => {
            this.uploadStatus = p.percent
          })
          .then(result => {
            this.contentId = result.contentId
            this.uploadStatus = null
            this.pollProcessing = true
            mixpanel.track(event.FILE_UPLOADED)
            metrics.sendMetric(metrics.CATEGORIES.THIRD_PARTY_CALLS, metrics.CP_TAGS.UPLOAD_RESOURCE_SUCCESS, 1, metrics.TYPE.OCCURRENCE_COUNTER)
            this.pollResourceProcessing(result.transactionId)
          })
          .catch(err => {
            let errorOptions = this.$t('error.uploadError')
            let error = uploadError.resolveError(err, this.file, errorOptions)
            this.$emit('uploadError', error)
          })
      },
      updateResource() {
        this.processingStatus = null
        csSdkManager.getCSClient({accessToken: this.accessToken}).updateResource(this.resourceId, this.file)
          .progress(p => {
            this.uploadStatus = p.percent
          })
          .then(result => {
            this.uploadStatus = null
            this.pollProcessing = true
            mixpanel.track(event.FILE_UPDATED)
            metrics.sendMetric(metrics.CATEGORIES.THIRD_PARTY_CALLS, metrics.CP_TAGS.UPDATE_RESOURCE_SUCCESS, 1, metrics.TYPE.OCCURRENCE_COUNTER)
            this.pollResourceProcessing(result.transactionId)
          })
          .catch(err => {
            let error = uploadError.resolveError(err, this.file)
            this.$emit('uploadError', error)
          })
      },
      pollResourceProcessing (transactionId) {
        csSdkManager.getCSClient({accessToken: this.accessToken}).transactionStatus(transactionId)
          .progress(p => {
            switch (p.target.response) {
              case '"processing"':
                this.processingStatus = this.$t('general.processing')
                break
              case '"processed"':
                this.processingStatus = this.$t('general.processed')
                break
              case '"queued"':
                this.processingStatus = this.$t('general.processing')
                break
            }
            if (this.pollProcessing) {
              setTimeout(() => {
                this.pollResourceProcessing(transactionId)
              }, 3000)
            }
          })
          .then(result => {
            if (result.body === 'processed') {
              this.pollProcessing = false
              mixpanel.track(event.FILE_PROCESSED)
              metrics.sendMetric(metrics.CATEGORIES.THIRD_PARTY_CALLS, metrics.CP_TAGS.RESOURCE_PROCESSING_SUCCESS, 1, metrics.TYPE.OCCURRENCE_COUNTER)
              this.$emit('processingStatus', result.body)
            } else if (result.body === 'failed') {
              this.processingStatus = this.$t('general.failed')
              mixpanel.track(event.FILE_PROCESSING_FAILED)
              metrics.sendMetric(metrics.CATEGORIES.THIRD_PARTY_CALLS, metrics.CP_TAGS.RESOURCE_PROCESSING_ERROR, 1, metrics.TYPE.FAILURE_COUNTER)
              let error = uploadError.resolveError(result, this.file, this.$t('error.uploadError'))
              this.$emit('uploadError', error)
            }
          })
          .catch(err => {
            metrics.sendMetric(metrics.CATEGORIES.THIRD_PARTY_CALLS, metrics.CP_TAGS.UPLOAD_RESOURCE_ERROR, 1, metrics.TYPE.FAILURE_COUNTER, err)
            this.pollProcessing = false
            let error = uploadError.resolveError(err, this.file)
            this.$emit('uploadError', error)
          })
      },
      resolveHover(hover) {
        this.hover = hover
      }
    },
    computed: {
      ...mapGetters(['accessToken']),
      fileName: {
        get: function () {
          if (this.file) {
            return fileUtil.fileName(this.file)
          }
        }
      },
      status() {
        if (this.uploadStatus) {
          return this.$t('general.uploading') + ' ' + Math.round(this.uploadStatus) + '%'
        } else if (this.processingStatus) {
          return this.processingStatus
        }
      }
    },
    watch: {
      file(newFile) {
        if (newFile) {
          if (!this.resourceId) {
            mixpanel.track(event.FILE_ADDED_FOR_UPLOAD)
            this.uploadResource()
          } else {
            this.updateResource()
          }
        }
      },
      contentId(value, newValue) {
        this.$emit('contentId', this.contentId)
      }
    }
  }
</script>

<style scoped lang="scss">
  @import '../../../../styles/colours';
  @import '../../../../styles/breakpoints';

  progress.uk-progress.bar {
    height: 8px;
  }

  .bar {
    height: 6px;
    width: 100%;
    position: relative;
    overflow: hidden;
    background-color: $lightest-gray;
    margin-top: 0.75rem;
    border: 1px solid $light-gray;
  }

  .file-name-style {
    font-size: 16px;
    color: $content-text;
  }

  .loader:before {
    display: block;
    position: absolute;
    content: "";
    left: -200px;
    width: 200px;
    height: 6px;
    background-color: $light-blue;
    animation: loading 2s linear infinite;
  }

  .progress-bar-container {
    margin-top: 10px;
    margin-right: 13px;
    height: 6px;
  }

  .uploader-container {
    max-width: 600px;
    min-height: 104px;
    border-radius: 8px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.24);
    background-color: white;
  }

  .upload-progress-container {
    padding: 30px 13px 15px 30px;
  }

  .upload-progress-style {
    font-size: 14px;
    font-weight: bold;
    color: $content-text;
  }

  @keyframes loading {
    from {left: -200px; width: 30%;}
    50% {width: 30%;}
    70% {width: 70%;}
    80% { left: 50%;}
    95% {left: 120%;}
    to {left: 100%;}
  }

</style>
